/**
 * Analytics Library - NEX Hub
 * Centralized analytics tracking for Google Analytics 4, Hotjar, and Mixpanel
 */

// ============================================================================
// GOOGLE ANALYTICS 4
// ============================================================================

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

/**
 * Initialize Google Analytics 4
 */
export function initGA4(measurementId: string) {
  if (typeof window === 'undefined') return;

  // Create script tag
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer?.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', measurementId);

  console.log('✅ Google Analytics 4 initialized');
}

/**
 * Track custom event in GA4
 */
export function trackEvent(eventName: string, params?: Record<string, any>) {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('event', eventName, params);
  console.log('📊 GA4 Event:', eventName, params);
}

// ============================================================================
// HOTJAR
// ============================================================================

declare global {
  interface Window {
    hj?: (...args: any[]) => void;
    _hjSettings?: { hjid: number; hjsv: number };
  }
}

/**
 * Initialize Hotjar
 */
export function initHotjar(hjid: number, hjsv: number = 6) {
  if (typeof window === 'undefined') return;

  (function (h: any, o: any, t: any, j: any, a: any, r: any) {
    h.hj =
      h.hj ||
      function () {
        (h.hj.q = h.hj.q || []).push(arguments);
      };
    h._hjSettings = { hjid: hjid, hjsv: hjsv };
    a = o.getElementsByTagName('head')[0];
    r = o.createElement('script');
    r.async = 1;
    r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
    a.appendChild(r);
  })(window, document, 'https://static.hotjar.com/c/hotjar-', '.js?sv=');

  console.log('✅ Hotjar initialized');
}

// ============================================================================
// MIXPANEL
// ============================================================================

declare global {
  interface Window {
    mixpanel?: any;
  }
}

/**
 * Initialize Mixpanel
 */
export function initMixpanel(token: string) {
  if (typeof window === 'undefined') return;

  (function (c: any, a: any) {
    if (!a.__SV) {
      var b = window;
      try {
        var d: any,
          m: any,
          j: any,
          k = b.location,
          f = k.hash;
        d = function (a: any, b: any) {
          return (m = a.match(RegExp(b + '=([^&]*)'))) ? m[1] : null;
        };
        f &&
          d(f, 'state') &&
          ((j = JSON.parse(decodeURIComponent(d(f, 'state')))),
          'mpeditor' === j.action &&
            (b.sessionStorage.setItem('_mpcehash', f),
            history.replaceState(
              j.desiredHash || '',
              (c as any).title,
              k.pathname + k.search
            )));
      } catch (n) {}
      var l: any, h: any;
      window.mixpanel = a;
      a._i = [];
      a.init = function (b: any, d: any, g: any) {
        function c(b: any, i: any) {
          var a = i.split('.');
          2 == a.length && ((b = b[a[0]]), (i = a[1]));
          b[i] = function () {
            b.push([i].concat(Array.prototype.slice.call(arguments, 0)));
          };
        }
        var e = a;
        'undefined' !== typeof g ? (e = a[g] = []) : (g = 'mixpanel');
        e.people = e.people || [];
        e.toString = function (b: any) {
          var a = 'mixpanel';
          'mixpanel' !== g && (a += '.' + g);
          b || (a += ' (stub)');
          return a;
        };
        e.people.toString = function () {
          return e.toString(1) + '.people (stub)';
        };
        l =
          'disable time_event track track_pageview track_links track_forms track_with_groups add_group set_group remove_group register register_once alias unregister identify name_tag set_config reset opt_in_tracking opt_out_tracking has_opted_in_tracking has_opted_out_tracking clear_opt_in_out_tracking start_batch_senders people.set people.set_once people.unset people.increment people.append people.union people.track_charge people.clear_charges people.delete_user people.remove'.split(
            ' '
          );
        for (h = 0; h < l.length; h++) c(e, l[h]);
        var f = 'set set_once union unset remove delete'.split(' ');
        e.get_group = function () {
          function a(c: any) {
            b[c] = function () {
              call2_args = arguments;
              call2 = [c].concat(Array.prototype.slice.call(call2_args, 0));
              e.push([d, call2]);
            };
          }
          for (
            var b = {},
              d = ['get_group'].concat(
                Array.prototype.slice.call(arguments, 0)
              ),
              c = 0;
            c < f.length;
            c++
          )
            a(f[c]);
          return b;
        };
        a._i.push([b, d, g]);
      };
      a.__SV = 1.2;
      b = c.createElement('script');
      b.type = 'text/javascript';
      b.async = !0;
      b.src =
        'undefined' !== typeof MIXPANEL_CUSTOM_LIB_URL
          ? MIXPANEL_CUSTOM_LIB_URL
          : 'file:' === c.location.protocol &&
            '//cdn.mxpnl.com/libs/mixpanel-2-latest.min.js'.match(/^\/\//)
          ? 'https://cdn.mxpnl.com/libs/mixpanel-2-latest.min.js'
          : '//cdn.mxpnl.com/libs/mixpanel-2-latest.min.js';
      d = c.getElementsByTagName('script')[0];
      d.parentNode.insertBefore(b, d);
    }
  })(document, window.mixpanel || []);

  window.mixpanel.init(token);
  console.log('✅ Mixpanel initialized');
}

/**
 * Track event in Mixpanel
 */
export function trackMixpanelEvent(eventName: string, properties?: Record<string, any>) {
  if (typeof window === 'undefined' || !window.mixpanel) return;

  window.mixpanel.track(eventName, properties);
  console.log('📊 Mixpanel Event:', eventName, properties);
}

/**
 * Identify user in Mixpanel
 */
export function identifyMixpanelUser(userId: string, traits?: Record<string, any>) {
  if (typeof window === 'undefined' || !window.mixpanel) return;

  window.mixpanel.identify(userId);
  if (traits) {
    window.mixpanel.people.set(traits);
  }
  console.log('👤 Mixpanel User Identified:', userId);
}

// ============================================================================
// UNIFIED ANALYTICS API
// ============================================================================

/**
 * Initialize all analytics services
 */
export function initAnalytics() {
  // Get IDs from environment variables
  const GA4_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID;
  const HOTJAR_ID = import.meta.env.VITE_HOTJAR_ID;
  const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN;

  if (GA4_ID) {
    initGA4(GA4_ID);
  } else {
    console.warn('⚠️  GA4 ID not configured');
  }

  if (HOTJAR_ID) {
    initHotjar(parseInt(HOTJAR_ID));
  } else {
    console.warn('⚠️  Hotjar ID not configured');
  }

  if (MIXPANEL_TOKEN) {
    initMixpanel(MIXPANEL_TOKEN);
  } else {
    console.warn('⚠️  Mixpanel token not configured');
  }
}

// ============================================================================
// CUSTOM EVENTS FOR NEX HUB
// ============================================================================

/**
 * Track NEX installation click
 */
export function trackNEXInstallClick(source: string) {
  trackEvent('nex_install_click', {
    source,
    timestamp: new Date().toISOString(),
  });
  trackMixpanelEvent('NEX Install Click', { source });
}

/**
 * Track agent view
 */
export function trackAgentView(agentId: string, agentName: string, category: string) {
  trackEvent('agent_view', {
    agent_id: agentId,
    agent_name: agentName,
    category,
  });
  trackMixpanelEvent('Agent Viewed', {
    agent_id: agentId,
    agent_name: agentName,
    category,
  });
}

/**
 * Track agent install intent
 */
export function trackAgentInstallIntent(agentId: string, agentName: string) {
  trackEvent('agent_install_intent', {
    agent_id: agentId,
    agent_name: agentName,
  });
  trackMixpanelEvent('Agent Install Intent', {
    agent_id: agentId,
    agent_name: agentName,
  });
}

/**
 * Track documentation view
 */
export function trackDocsView(page: string) {
  trackEvent('docs_view', {
    page,
  });
  trackMixpanelEvent('Docs Viewed', { page });
}

/**
 * Track GitHub click
 */
export function trackGitHubClick(source: string) {
  trackEvent('github_click', {
    source,
  });
  trackMixpanelEvent('GitHub Clicked', { source });
}

/**
 * Track user signup
 */
export function trackUserSignup(method: string) {
  trackEvent('user_signup', {
    method,
  });
  trackMixpanelEvent('User Signup', { method });
}

/**
 * Track page view (for SPA)
 */
export function trackPageView(page: string) {
  trackEvent('page_view', {
    page_path: page,
    page_title: document.title,
  });
  trackMixpanelEvent('Page Viewed', {
    page_path: page,
    page_title: document.title,
  });
}

/**
 * Track search
 */
export function trackSearch(query: string, results: number) {
  trackEvent('search', {
    search_term: query,
    results_count: results,
  });
  trackMixpanelEvent('Search Performed', {
    query,
    results,
  });
}

/**
 * Track filter change
 */
export function trackFilterChange(filterType: string, filterValue: string) {
  trackEvent('filter_change', {
    filter_type: filterType,
    filter_value: filterValue,
  });
  trackMixpanelEvent('Filter Changed', {
    type: filterType,
    value: filterValue,
  });
}

// ============================================================================
// EXPORT ALL
// ============================================================================

export const analytics = {
  init: initAnalytics,
  trackEvent,
  trackPageView,
  trackNEXInstallClick,
  trackAgentView,
  trackAgentInstallIntent,
  trackDocsView,
  trackGitHubClick,
  trackUserSignup,
  trackSearch,
  trackFilterChange,
  identifyUser: identifyMixpanelUser,
};

export default analytics;
